import { useEffect, useState } from "react";
import { api, type QueueItem, type Settlement } from "../lib/api";
import { measure } from "../lib/thumbs";
import { AddMembers } from "./AddMembers";

// The settlements tab. A settlement is a thing you build up here -- create it,
// give it members, see whether it works -- instead of a word typed identically
// onto eight separate builds, which is what it used to be.
//
// It only works as a SET, and none of that is visible on a single build:
//   * without an anchor it is never placed at all,
//   * without members it is a village of one,
//   * and one oversized member thins out the whole settlement, because the
//     spawner scales the spiral pitch with the largest member footprint.
// So the checks live next to the settlement, not next to a text field.

export function SettlementTab(props: {
  items: QueueItem[];
  onPick: (id: number) => void;
  say: (m: string) => void;
  onChanged: () => void;
}) {
  const [list, setList] = useState<Settlement[] | null>(null);
  const [max, setMax] = useState(9);
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState<{ id: number; role: number } | null>(null);
  const [newName, setNewName] = useState("");

  const load = async () => {
    const r = await api.settlements();
    setList(r.settlements);
    setMax(r.maxMemberFootprint);
    return r;
  };

  useEffect(() => {
    void (async () => {
      const r = await load();
      // The server has no .schem parser and should not grow one, so a build's
      // extents are only known once a client has looked at it. It names the
      // members it is missing; measuring those here means the footprint
      // warning is complete the first time this tab is opened.
      if (r.unmeasured.length === 0) return;
      let done = 0;
      for (const id of r.unmeasured) {
        setNote(`messe ${++done}/${r.unmeasured.length} Bauten…`);
        await measure(id);
      }
      setNote("");
      await load();
    })();
  }, []);

  const run = async (what: () => Promise<{ settlements: Settlement[] }>) => {
    try {
      setList((await what()).settlements);
      props.onChanged();
    } catch (e) {
      props.say(String((e as Error).message));
    }
  };

  const byId = new Map(props.items.map((i) => [i.id, i]));

  return (
    <div className="settlements">
      <div className="create">
        <input
          placeholder="Name, z. B. „Fischerdorf“"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || !newName.trim()) return;
            void run(() => api.createSettlement(newName.trim())).then(() =>
              setNewName(""),
            );
          }}
        />
        <button
          className="primary"
          disabled={!newName.trim()}
          onClick={() =>
            void run(() => api.createSettlement(newName.trim())).then(() =>
              setNewName(""),
            )
          }
        >
          + anlegen
        </button>
      </div>

      <p className="hint">
        Ein Anker eröffnet die Siedlung, die Mitglieder gruppieren sich darum.
        Mitglieder über {max} Blöcke lassen sie schrumpfen.
      </p>
      {note && <p className="hint">{note}</p>}

      {list === null && <p className="hint">lädt…</p>}
      {list?.length === 0 && (
        <p className="hint">Noch keine Siedlung — leg oben eine an.</p>
      )}

      {list?.map((st) => {
        const anchors = st.members.filter((m) => m.role === 1);
        const members = st.members.filter((m) => m.role === 2);
        return (
          <section
            key={st.id}
            className={st.problems.length ? "card broken" : "card"}
          >
            <h3>
              <input
                className="title"
                defaultValue={st.name}
                key={`n${st.id}-${st.name}`}
                onBlur={(e) =>
                  e.target.value.trim() !== st.name &&
                  void run(() =>
                    api.editSettlement(st.id, { name: e.target.value.trim() }),
                  )
                }
              />
              <span className="counts">
                ⚓ {anchors.length} · ▪ {members.length}
              </span>
              <button
                className="linkish danger"
                title="Löst die Siedlung auf; die Bauten werden wieder Einzelbauten."
                onClick={() => void run(() => api.deleteSettlement(st.id))}
              >
                auflösen
              </button>
            </h3>

            <input
              className="note"
              placeholder="Notiz"
              defaultValue={st.note}
              key={`no${st.id}`}
              onBlur={(e) =>
                e.target.value !== st.note &&
                void run(() => api.editSettlement(st.id, { note: e.target.value }))
              }
            />

            {st.problems.map((p, i) => (
              <p className="problem" key={i}>
                ⚠ {p}
              </p>
            ))}

            {([1, 2] as const).map((role) => {
              const rows = role === 1 ? anchors : members;
              return (
                <div className="slot" key={role}>
                  <h4>
                    {role === 1 ? "⚓ Anker" : "▪ Mitglieder"}
                    <button
                      className="linkish"
                      onClick={() => setAdding({ id: st.id, role })}
                    >
                      + hinzufügen
                    </button>
                  </h4>
                  <ul>
                    {rows.map((m) => (
                      <li
                        key={m.id}
                        className={
                          role === 2 && m.size > max ? "oversized" : undefined
                        }
                      >
                        <button
                          className="linkish name"
                          title="im Betrachter öffnen"
                          onClick={() => props.onPick(m.id)}
                        >
                          {m.name}
                        </button>
                        <span className="where">
                          {byId.get(m.id)?.folder ?? m.folder}
                        </span>
                        {m.size > 0 && (
                          <span className="size">{m.size} Blöcke</span>
                        )}
                        <button
                          className="linkish"
                          title={
                            role === 1
                              ? "zum Mitglied machen"
                              : "zum Anker machen"
                          }
                          onClick={() =>
                            void run(() =>
                              api.addMembers(st.id, [m.id], role === 1 ? 2 : 1),
                            )
                          }
                        >
                          {role === 1 ? "→ Mitglied" : "→ Anker"}
                        </button>
                        <button
                          className="linkish danger"
                          title="aus der Siedlung nehmen"
                          onClick={() =>
                            void run(() => api.removeMembers(st.id, [m.id]))
                          }
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                    {rows.length === 0 && (
                      <li className="hint">
                        {role === 1
                          ? "keiner — so wird hier nichts platziert"
                          : "keine"}
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </section>
        );
      })}

      {adding && (
        <AddMembers
          items={props.items}
          taken={new Set((list ?? []).flatMap((s) => s.members.map((m) => m.id)))}
          role={adding.role}
          onClose={() => setAdding(null)}
          onAdd={async (ids) => {
            await run(() => api.addMembers(adding.id, ids, adding.role));
            setAdding(null);
          }}
        />
      )}
    </div>
  );
}
